import { CheckIcon } from 'lucide-react';
import React from 'react';
import { Text } from '../ui';

/**
 * Simple Stepper component for onboarding flows.
 * Props:
 * - steps: Array<{ title: string, description?: string }>
 * - active: number (0-based index of active/current step)
 * - onStepClick?: (index) => void
 * - className?: additional classes for wrapper
 */
const Stepper = ({ steps = [], active = 0, className = '' }) => {
  const progressPct = steps.length ? Math.round(((active + 1) / steps.length) * 100) : 0;
  const currentStep = steps[active] || {};

  return (
    <nav
      className={`w-full ${className} mb-2  md:mb-10 px-0 sm:px-2 md:px-16`}
      aria-label='Onboarding Steps'
    >
      <ol className='flex flex-col gap-2 items-start w-full relative min-w-0'>
        <div className='relative flex w-full items-start'>
          {steps.map((step, idx) => {
            const isActive = idx === active;
            const isCompleted = idx < active;

            return (
              <React.Fragment key={idx}>
                {/* Step item */}
                <li className='flex flex-col items-center relative z-10 min-w-0'>
                  {/* Step number circle */}
                  <div
                    className={`flex items-center justify-center h-10 w-10 rounded-full border-2 flex-shrink-0 transition-colors duration-150 pt-1`}
                    style={
                      isCompleted
                        ? { backgroundColor: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' }
                        : isActive
                        ? { backgroundColor: 'var(--bg-card)', borderColor: 'var(--brand)', color: 'var(--brand)' }
                        : { backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)', color: 'var(--text-muted)' }
                    }
                  >
                    {isCompleted ? (
                      <CheckIcon className='h-6 w-6 text-white' />
                    ) : (
                      <Text className='font-medium !text-inherit text-xl'>{idx + 1}</Text>
                    )}
                  </div>

                  {/* Step text - only show for active step on mobile */}
                  <div className='absolute top-10 mt-2 w-[100px] sm:w-fit text-center px-2'>
                    {/* Show all titles on desktop */}
                    <div className='hidden sm:block'>
                      <Text
                        className={`text-sm truncate ${isActive ? 'font-bold' : 'font-medium'}`}
                      >
                        {step.title}
                      </Text>
                      {step.description && <Text variant='caption'>{step.description}</Text>}
                    </div>
                  </div>
                </li>

                {/* Progress line between steps */}
                {idx < steps.length - 1 && (
                  <div
                    className='flex-1 h-0.5 self-start mt-5 transition-colors duration-300'
                    style={{ backgroundColor: idx < active ? 'var(--brand)' : 'var(--border)' }}
                    aria-hidden='true'
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <li className='w-full md:hidden'>
          <div className='flex-1 justify-between items-center flex'>
            <div className='inline-flex'>
              <Text className={`text-sm truncate font-bold`}>{currentStep.title}</Text>
              {currentStep.description && <Text variant='caption'>{currentStep.description}</Text>}
            </div>

            <Text variant='captionNeutral' className='inline-flex text-sm' style={{ color: 'var(--text-muted)' }}>
              Step {active + 1} of {steps.length}
            </Text>
          </div>
        </li>
      </ol>
    </nav>
  );
};

export default Stepper;
